import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H1, H2, Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';

import { useMessages } from '../../shared/i18n/useMessages';
import { useReviewWizard } from '../../features/audit-whiz/hooks/useReviewWizard';
import { AstraQuestionsPanel } from '../../features/audit-whiz/components/AstraQuestionsPanel';
import { ReviewWizardStepper } from '../../features/audit-whiz/components/ReviewWizardStepper';
import { AuditCriteriaStep } from '../../features/audit-whiz/components/AuditCriteriaStep';
import { SubQuestionsStep } from '../../features/audit-whiz/components/SubQuestionsStep';
import { QuestionConfigurationStep } from '../../features/audit-whiz/components/QuestionConfigurationStep';
import {
  ReviewQuestionStatus,
  ReviewWizardStep,
  type ReviewQuestion,
} from '../../features/audit-whiz/logic/reviewTypes';

const PAGE_BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type PageKey =
  | 'pageTitle'
  | 'comingSoon'
  | 'criteriaStepHeading'
  | 'subStepHeading'
  | 'configStepHeading'
  | 'backButton'
  | 'nextButton'
  | 'skipForNowButton'
  | 'publishButton'
  | 'pageSubtitle'
  | 'metaSummary'
  | 'newPromptButton'
  | 'questionPositionLabel'
  | 'draftPill';

const TESTID = 'review-questions';

/** Generate screen — the "New Prompt & Source" button returns here. */
const GENERATE_ROUTE = '/question/generate';

/** One footer action button's config. Rendered by {@link ActionButton} so the
 *  page's four buttons share a single JSX site instead of duplicating markup. */
interface ActionConfig {
  readonly testId: string;
  readonly label: string;
  readonly variant: ButtonVariant;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}

/** Renders one footer action from its config. */
function ActionButton({ action }: { readonly action: ActionConfig }): JSX.Element {
  return (
    <Button
      variant={action.variant}
      disabled={action.disabled ?? false}
      onClick={action.onClick}
      data-testid={action.testId}
    >
      {action.label}
    </Button>
  );
}

/**
 * "Review & publish Astra questions" screen (AuditWhiz WHIZ-002).
 *
 * Two-column review layout: the 3-step wizard (stepper + active-step editor +
 * action bar) on the left, the Astra-questions side panel on the right. Fully
 * driven by `useReviewWizard`, which owns the draft list, selection, and step
 * cursor and self-seeds from mock data (Phase 1 — no agent yet).
 *
 * Rendered on desktop through `V2Adapter` (see AppRouter.tsx), which supplies
 * the Stencil, i18n, and auth providers this page needs.
 */
export function ReviewQuestionsPage(): JSX.Element {
  const { t, f } = useMessages<PageKey>(PAGE_BUNDLE);
  const navigate = useNavigate();
  const wizard = useReviewWizard();

  const { questions } = wizard;
  const total = questions.length;
  const pendingCount = questions.filter(
    (q) => q.status === ReviewQuestionStatus.NEEDS_REVIEW,
  ).length;
  const publishedCount = total - pendingCount;

  const handleNewPrompt = (): void => {
    void navigate(GENERATE_ROUTE);
  };

  const { selectedQuestion } = wizard;
  // Published drafts are read-only on the review screen.
  const isPublished = selectedQuestion?.status === ReviewQuestionStatus.PUBLISHED;

  // 1-based position of the draft ACTUALLY rendered. Derived from
  // `selectedQuestion?.id`, not `selectedId`: the hook resolves selectedQuestion
  // with a `?? questions.at(0)` fallback when selectedId doesn't match an entry
  // (selection drift after a delete). Using selectedId here would give -1 → a
  // "Question 0 of N" header while question 1 is on screen. 0 only when the list
  // is empty (no question rendered).
  const selectedPosition = questions.findIndex((q) => q.id === selectedQuestion?.id) + 1;

  // Status pill beside the wizard header: the published Question ID once saved,
  // otherwise the "draft — no ID yet" copy.
  const publishedQuestionId = selectedQuestion?.questionId;
  const statusPillType = isPublished ? BadgeType.Positive : BadgeType.Neutral;
  const statusPillValue =
    isPublished && publishedQuestionId ? publishedQuestionId : t('draftPill');

  // Stepper labels keyed by the wizard-step enum (Map, not object, so the
  // union-typed lookup doesn't trip security/detect-object-injection).
  //
  // NOT memoized: `t` is referentially stable across renders (see useMessages),
  // so a `useMemo(..., [t])` would compute exactly once — on the first render,
  // before the i18n bundle resolves, when `t(key)` still returns the raw key —
  // and then never recompute, freezing the raw keys into the labels. Building
  // the 3-entry Map inline each render re-calls `t` after the bundle resolves.
  const stepLabels: ReadonlyMap<ReviewWizardStep, string> = new Map([
    [ReviewWizardStep.AUDIT_CRITERIA, t('criteriaStepHeading')],
    [ReviewWizardStep.SUB_QUESTIONS, t('subStepHeading')],
    [ReviewWizardStep.QUESTION_CONFIGURATION, t('configStepHeading')],
  ]);

  const skipAction: ActionConfig = {
    testId: `${TESTID}-skip`,
    label: t('skipForNowButton'),
    variant: ButtonVariant.Tertiary,
    onClick: wizard.skipForNow,
  };
  const stepActions: readonly ActionConfig[] = [
    {
      testId: `${TESTID}-back`,
      label: t('backButton'),
      variant: ButtonVariant.Secondary,
      onClick: wizard.goPrev,
      disabled: wizard.isFirstStep,
    },
    {
      testId: `${TESTID}-next`,
      label: t('nextButton'),
      variant: ButtonVariant.Secondary,
      onClick: wizard.goNext,
      disabled: wizard.isLastStep,
    },
    {
      testId: `${TESTID}-publish`,
      label: t('publishButton'),
      variant: ButtonVariant.Primary,
      onClick: wizard.publishSelected,
      disabled: Boolean(isPublished),
    },
  ];

  return (
    <Col
      gridGap="dimensions.spacing.400"
      width="100%"
      padding={{ top: 'dimensions.spacing.300', bottom: 'dimensions.spacing.600' }}
      data-testid={`${TESTID}-page`}
    >
      {/* Full-width page header — title + count and the New Prompt & Source
          action on one row (title left, action far top-right), then the
          description and meta summary. Spans the whole page above the columns,
          matching the mock. */}
      <Col gridGap="dimensions.spacing.100">
        <Row justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
          <Row gridGap="dimensions.spacing.200" alignItems="center">
            <H1>{t('pageTitle')}</H1>
            <Badge type={BadgeType.Neutral} value={String(total)} />
          </Row>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleNewPrompt}
            data-testid={`${TESTID}-new-prompt`}
          >
            {t('newPromptButton')}
          </Button>
        </Row>
        <Text color="color.text.secondary">{t('pageSubtitle')}</Text>
        <Text fontSize="T100" color="color.text.secondary">
          {f('metaSummary', { pending: pendingCount, published: publishedCount })}
        </Text>
      </Col>

      {/* Two columns below the header: the wizard (2/3) and the Astra panel
          (1/3). */}
      <Row
        gridGap="dimensions.spacing.400"
        width="100%"
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <Col flex={2} minWidth={0} gridGap="dimensions.spacing.400">
          {/* One outer card wraps the wizard header, stepper, and active step —
              matching the mock, where all three read as a single bordered
              panel. The step components render card-less content (Audit
              criteria flat; Sub Questions / Configuration keep only their inner
              section cards), so there's no card-in-card. */}
          <Card padding="dimensions.spacing.400">
            <Col gridGap="dimensions.spacing.400" width="100%">
              {/* Wizard header — position + draft/published status pill. */}
              {selectedQuestion && (
                <Row gridGap="dimensions.spacing.200" alignItems="center">
                  <H2>
                    {f('questionPositionLabel', { current: selectedPosition, total })}
                  </H2>
                  <Badge type={statusPillType} value={statusPillValue} />
                </Row>
              )}

              {/* Inner gray band for the stepper (matches the mock's stepper
                  strip inside the card). */}
              <Card padding="dimensions.spacing.300">
                <ReviewWizardStepper
                  currentStep={wizard.currentStep}
                  onStepSelect={wizard.goToStep}
                  labels={stepLabels}
                />
              </Card>

              {renderActiveStep(
                wizard.currentStep,
                selectedQuestion,
                wizard.onQuestionChange,
                Boolean(isPublished),
              )}

              {/* Footer actions inside the card (matching the mock) — Skip on
                  the left, the step/publish trio on the right. All rendered
                  from one ActionButton so the four buttons don't duplicate
                  structure (DRY). */}
              <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
                <ActionButton action={skipAction} />
                <Row gridGap="dimensions.spacing.200" alignItems="center">
                  {stepActions.map((action) => (
                    <ActionButton key={action.testId} action={action} />
                  ))}
                </Row>
              </Row>
            </Col>
          </Card>
        </Col>

        <Col flex={1} minWidth={0} gridGap="dimensions.spacing.200">
          <AstraQuestionsPanel
            questions={wizard.questions}
            selectedId={wizard.selectedId}
            onSelect={wizard.selectQuestion}
            onDelete={wizard.deleteQuestion}
            dataTestId={`${TESTID}-panel`}
          />
        </Col>
      </Row>
    </Col>
  );

  /**
   * The editor for the active wizard step. Early-return helper (not a nested
   * ternary in JSX) per the workspace tsx-readability rule. Guards the
   * empty-list case where no draft is selected.
   */
  function renderActiveStep(
    step: ReviewWizardStep,
    question: ReviewQuestion | undefined,
    onQuestionChange: (next: ReviewQuestion) => void,
    disabled: boolean,
  ): ReactNode {
    if (!question) {
      return <Text color="color.text.secondary">{t('comingSoon')}</Text>;
    }
    const stepProps = {
      question,
      onQuestionChange,
      disabled,
      dataTestId: `${TESTID}-step`,
    };
    if (step === ReviewWizardStep.AUDIT_CRITERIA) {
      return <AuditCriteriaStep {...stepProps} />;
    }
    if (step === ReviewWizardStep.SUB_QUESTIONS) {
      return <SubQuestionsStep {...stepProps} />;
    }
    return <QuestionConfigurationStep {...stepProps} />;
  }
}
